import classNames from 'classnames/bind';
import { DateFormat } from 'common/types';
import { Days, days } from 'common/utils';
import useFarePrices from 'hooks/useFarePrices';
import useHolidays from 'hooks/useHolidays';
import { MouseEvent, useCallback, useState } from 'react';
import { useInfiniteRange } from 'react-awesome-calendar-ts';
import StayDuration from './StayDuration';
import StayDurationInput from './StayDurationInput';
import DateInput from './dateInput';
import styles from './index.module.scss';
import RemoveSelectedDates from './removeSelectedDates';
import { ICalendar } from './types';

const cx = classNames.bind(styles);

const buttonTitles = ['가는 날', '오는 날'];
const RoundTripCalendar = ({
  searchParams,
  isDomestic,
  setDepartureDate,
  setDepartureDates,
  setStayLength,
  closeModal,
  index,
}: ICalendar) => {
  const { itinerary, stayLength } = searchParams;
  const Calendar = useInfiniteRange();
  const [{ selectedDates, editingIndex, selectedStayLength }, setOptions] =
    useState({
      selectedDates: itinerary
        ? itinerary.map((iti, idx) =>
            iti?.departureDate && (idx !== 1 || !stayLength)
              ? days(iti.departureDate, DateFormat.YMD)
              : null,
          )
        : [],
      editingIndex: index,
      selectedStayLength: stayLength || '',
    });
  const [viewMonths, onViewMonthsUpdated] = useState<Days[]>([]);
  const holidays = useHolidays(viewMonths);
  const customDates = useFarePrices(searchParams, isDomestic, selectedDates[0]);
  const [isStayLengthType, toggleStayLengthType] =
    useState<boolean>(!!stayLength);

  const clearSelectedDates = useCallback(() => {
    setDepartureDates([]);
    setStayLength('');
    setOptions({
      selectedDates: [],
      editingIndex: 0,
      selectedStayLength: '',
    });
  }, []);

  const onSuccess = useCallback(
    (dates: Days[][]) => {
      const ds = dates?.[0] || [];
      if (!ds?.length) return;
      const nextIndex = itinerary.findIndex((_, i) => !ds[i]);
      setOptions(prev => ({
        selectedDates: ds,
        editingIndex: Math.max(nextIndex, 0),
        selectedStayLength: ds[1] ? '' : prev.selectedStayLength,
      }));
      if (isStayLengthType) {
        setDepartureDate(ds[0], 0);
      } else {
        setDepartureDates(ds);
      }

      if (nextIndex === -1) {
        setStayLength('');
      }

      if (nextIndex < 0 || (isStayLengthType && selectedStayLength)) {
        closeModal();
      }
    },
    [itinerary, selectedStayLength, isStayLengthType],
  );

  const onSelectDateInput = (idx: number) => (e: MouseEvent) => {
    setOptions(prev => ({ ...prev, editingIndex: idx }));
  };

  const onSelectStayDurInput = () => {
    setOptions(prev => ({ ...prev, editingIndex: 1 }));
    toggleStayLengthType(true);
  };

  const onSelectStayDur = (duration: string) => {
    const [dep] = selectedDates;
    setOptions({
      selectedDates: [dep, null],
      editingIndex: 0,
      selectedStayLength: duration,
    });
    setDepartureDate(dep, 0);
    setStayLength(duration);
    if (dep) {
      closeModal();
    }
  };

  return (
    <div className={cx('calendar')}>
      <div className={cx('schedules')}>
        <div className={cx('inner')}>
          {itinerary.map((iti, i) => {
            if (i === 1 && !isDomestic && isStayLengthType) {
              return (
                <StayDurationInput
                  key={`calendar_titles_${i}`}
                  clickCode="cal.calendar"
                  editing={editingIndex === i}
                  duration={selectedStayLength}
                  onSelect={onSelectStayDurInput}
                  cx={cx}
                />
              );
            }
            return (
              <DateInput
                key={`calendar_titles_${i}`}
                index={i}
                editing={editingIndex === i}
                dateInfo={selectedDates[i]}
                buttonTitles={buttonTitles}
                onSelect={onSelectDateInput(i)}
                cx={cx}
                clickCode={i === 0 ? 'cal.depdate1' : 'cal.depdate2'}
              />
            );
          })}
          {!isDomestic && (
            <button
              className={cx('toggle')}
              data-event-nlog="click"
              data-event-area-code={
                isStayLengthType ? 'cal.openswitch' : 'cal.openticket'
              }
              onClick={() => {
                toggleStayLengthType(!isStayLengthType);
              }}
            >
              {isStayLengthType ? '달력' : '미정'}
            </button>
          )}
        </div>
      </div>
      <div className={cx('content')}>
        {!isDomestic && isStayLengthType && editingIndex === 1 ? (
          <StayDuration
            curDur={selectedStayLength}
            onSelect={onSelectStayDur}
          />
        ) : (
          <div
            data-event-nlog="click"
            data-event-area-code="cal.calendar"
            className={cx('calendar-wrapper')}
          >
            <Calendar
              options={{
                dateFormat: DateFormat.YMD,
                dayTexts: ['일', '월', '화', '수', '목', '금', '토'],
                todayText: '오늘',
                selectionTextBuilder: ({ index, dates }) => {
                  const samePrevIndex = dates[0]
                    .slice(0, index)
                    .findIndex((d: Days) => d?.isSame(dates[0][index], 'day'));
                  if (index > 0 && samePrevIndex >= 0) {
                    return `당일`;
                  }
                  return index ? '오는 날' : '가는 날';
                },
                maxSelection: 1,
                maxMonths: 12,
                allowSame: true,
                ignoreOrder: false,
                cancelable: false,
                height: '100%',
                visibleMonthCount: 14,
                todaySelectable: isDomestic,
                amassCustomDates: false,
              }}
              mutableOptions={{
                selectedDates: [selectedDates],
                holidays,
                customDates,
                editingIndex,
              }}
              methods={{
                onViewMonthsUpdated,
                onSuccess,
              }}
            />
          </div>
        )}
      </div>
      <RemoveSelectedDates clearSelectedDates={clearSelectedDates} />
    </div>
  );
};

export default RoundTripCalendar;
